import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "supertokens-auth-react/recipe/session";
import { getTenantInfo } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { logoutUser, getSSOLastActive, updateSSOLastActive } from "@/services/auth";

const LOG_PREFIX = "[SSO Inactivity]";

const isValidTimeout = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * Hook that enforces SSO inactivity timeout based on `ssoUserRefreshMins` tenant config.
 *
 * Two mechanisms:
 * 1. **Tab close/reopen**: Records timestamp on `beforeunload`. On next app load,
 *    if elapsed time exceeds the configured timeout, the user is logged out.
 * 2. **Mouse leaves window**: Starts a timer when the mouse leaves the document.
 *    If the mouse doesn't return within the timeout, the user is logged out.
 *    Timer is cancelled when the mouse re-enters.
 *
 * Timeout is only active when `ssoUserRefreshMins` is a positive finite number.
 * Values of 0, null, undefined, NaN, or Infinity disable the feature.
 *
 * Note: `beforeunload` is registered only after the `enabled` flag flips to true
 * (i.e. after SuperTokens' session check resolves). A tab close during that
 * initial window will not refresh `ssoLastActive`, so the next visit's mount
 * check will see the pre-window timestamp.
 */
export const useSSOInactivityTimeout = (enabled: boolean) => {
  const location = useLocation();
  const navigate = useNavigate();
  const appState = useAppState();
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Capture pathname via ref so `performLogout` identity stays stable across
  // in-app navigation — prevents downstream callbacks/effects from re-running
  // every route change.
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  const performLogout = useCallback(async () => {
    console.info(`${LOG_PREFIX} Timeout exceeded, signing out user`);
    logoutUser();
    await signOut();
    navigate("/login", { state: { from: pathRef.current, expired: true } });
  }, [navigate]);

  // Resolve `ssoUserRefreshMins` from a single source of truth.
  // Prefer Redux app state (kept in sync via updateTenantInfo dispatch); fall
  // back to a direct `getTenantInfo()` fetch only when app state isn't populated
  // yet (mount check runs before AppProvider has loaded tenant info).
  const refreshMins = appState?.ssoUserRefreshMins;

  const resolveRefreshMins = useCallback(async (): Promise<number | undefined> => {
    if (refreshMins !== undefined) return refreshMins ?? undefined;
    try {
      const tenantInfo = await getTenantInfo();
      return tenantInfo?.ssoUserRefreshMins ?? undefined;
    } catch (e) {
      console.warn(`${LOG_PREFIX} Failed to fetch tenant info, skipping check`, e);
      return undefined;
    }
  }, [refreshMins]);

  // Check inactivity on mount (tab reopen scenario).
  const checkOnMount = useCallback(async (): Promise<boolean> => {
    const lastActiveTs = getSSOLastActive();
    if (!lastActiveTs) {
      console.debug(`${LOG_PREFIX} No previous activity recorded, skipping check`);
      return false;
    }

    const resolved = await resolveRefreshMins();
    if (!isValidTimeout(resolved)) {
      console.debug(
        `${LOG_PREFIX} ssoUserRefreshMins not configured or invalid (${resolved}), timeout disabled`
      );
      return false;
    }

    const inactiveMs = Date.now() - lastActiveTs;
    console.debug(
      `${LOG_PREFIX} Last active ${Math.round(inactiveMs / 1000)}s ago, timeout is ${resolved}m`
    );

    if (inactiveMs > resolved * 60 * 1000) {
      await performLogout();
      return true;
    }
    return false;
  }, [performLogout, resolveRefreshMins]);

  // Record timestamp when user leaves/closes the tab
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      console.debug(`${LOG_PREFIX} Tab closing, recording last active timestamp`);
      updateSSOLastActive();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isValidTimeout(refreshMins)) return;

    console.debug(`${LOG_PREFIX} Mouse inactivity timeout set to ${refreshMins}m`);

    const handleMouseLeave = () => {
      console.debug(`${LOG_PREFIX} Mouse left window, starting ${refreshMins}m timer`);
      inactivityTimerRef.current = setTimeout(
        () => {
          performLogout();
        },
        refreshMins * 60 * 1000
      );
    };

    const handleMouseEnter = () => {
      if (inactivityTimerRef.current) {
        console.debug(`${LOG_PREFIX} Mouse returned, cancelling timer`);
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };

    document.documentElement.addEventListener("mouseleave", handleMouseLeave);
    document.documentElement.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      document.documentElement.removeEventListener("mouseleave", handleMouseLeave);
      document.documentElement.removeEventListener("mouseenter", handleMouseEnter);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [enabled, refreshMins, performLogout]);

  return { checkOnMount };
};
